<template>
  <div class="bill-receipt__wrapper">
    <base-popup
      class="bill-receipt"
      ref="popup"
      v-show="!notificationShowing && !sendToEmailPopupShowing"
      :without-icon="true"
    >
      <template #header="{ close }">
        <div class="bill-receipt__header">
          <popup-close-button
            class="bill-receipt__close"
            @click="close"
          ></popup-close-button>
        </div>
      </template>
      <template #content>
        <vue-pdf-embed :source="pdfBlob" />
      </template>
      <template #actions>
        <div class="bill-receipt__actions">
          <primary-button
            class="bill-receipt__actions-item"
            @click="shareReceipt"
            :label="$t('bills.popup.bill-receipt.actions.share')"
          ></primary-button>
          <no-bg-button
            :icon="downloadIcon"
            @click="downloadReceipt"
            :label="$t('bills.popup.bill-receipt.actions.download')"
          ></no-bg-button>
        </div>
      </template>
    </base-popup>
    <share-bill-receipt-popup
      :bill="bill"
      ref="shareBillReceiptPopup"
    ></share-bill-receipt-popup>
    <success-notification
      ref="billReceiptDownloadedNotification"
      :text="$t('bills.popup.bill-receipt.notifications.downloaded')"
    ></success-notification>
  </div>
</template>

<script>
import BasePopup from '@vue-app/components/base/popup/BasePopup.vue'
import PopupCloseButton from '@vue-app/components/base/button/PopupCloseButton.vue'
import SocialShareButtons from '@vue-app/components/base/share/SocialShareButtons.vue'
import LinkInput from '@vue-app/components/base/input/LinkInput.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import { Bill } from '@js-app/entities/Bill'
import VuePdfEmbed from 'vue-pdf-embed'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import NoBgButton from '@vue-app/components/base/button/NoBgButton.vue'
import ShareBillReceiptPopup from '@vue-app/components/company/bills/popup/ShareBillReceiptPopup.vue'

export default {
  name: 'BillReceiptPopup',
  components: {
    ShareBillReceiptPopup,
    NoBgButton,
    PrimaryButton,
    SuccessNotification,
    LinkInput,
    SocialShareButtons,
    PopupCloseButton,
    BasePopup,
    VuePdfEmbed,
  },
  props: {
    bill: Bill,
    pdfBlob: {
      type: [Blob, String, Object, Array],
    },
  },

  data() {
    return {
      notificationShowing: false,
      sendToEmailPopupShowing: false,
      downloadIcon: `<svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 11.625C1.55228 11.625 2 12.0727 2 12.625V14.75C2 14.8605 2.0439 14.9665 2.12204 15.0446C2.20018 15.1228 2.30616 15.1667 2.41667 15.1667H12.3333C12.4438 15.1667 12.5498 15.1228 12.628 15.0446C12.7061 14.9665 12.75 14.8605 12.75 14.75V12.625C12.75 12.0727 13.1977 11.625 13.75 11.625C14.3023 11.625 14.75 12.0727 14.75 12.625V14.75C14.75 15.3909 14.4954 16.0056 14.0422 16.4588C13.589 16.9121 12.9743 17.1667 12.3333 17.1667H2.41667C1.77573 17.1667 1.16104 16.9121 0.707825 16.4588C0.254612 16.0056 0 15.3909 0 14.75V12.625C0 12.0727 0.447715 11.625 1 11.625Z" fill="#64748B"/><path fill-rule="evenodd" clip-rule="evenodd" d="M3.83489 7.37688C4.22541 6.98635 4.85857 6.98635 5.2491 7.37688L7.37533 9.5031L9.50155 7.37688C9.89208 6.98635 10.5252 6.98635 10.9158 7.37688C11.3063 7.7674 11.3063 8.40057 10.9158 8.79109L8.08243 11.6244C7.8949 11.812 7.64054 11.9173 7.37533 11.9173C7.11011 11.9173 6.85576 11.812 6.66822 11.6244L3.83489 8.79109C3.44436 8.40057 3.44436 7.7674 3.83489 7.37688Z" fill="#64748B"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.375 0C7.92728 0 8.375 0.447715 8.375 1V10.9167C8.375 11.469 7.92728 11.9167 7.375 11.9167C6.82272 11.9167 6.375 11.469 6.375 10.9167V1C6.375 0.447715 6.82272 0 7.375 0Z" fill="#64748B"/></svg>`,
    }
  },
  methods: {
    open() {
      return this.$refs.popup.open()
    },
    async handleLinkCopied() {
      this.notificationShowing = true
      await this.$refs.billLinkCopiedNotification.show()

      this.notificationShowing = false
    },
    shareReceipt() {
      this.$refs.shareBillReceiptPopup.open()
    },
    downloadReceipt() {
      downloadBlob(this.pdfBlob, `${this.bill.partnerTransactionId}.pdf`)
      this.$refs.billReceiptDownloadedNotification.show()
    },
  },
}
</script>

<style lang="scss" scoped>
.bill-receipt {
  :deep(.popup) {
    width: 770px;
    max-width: 100%;
    max-height: 80vh;
    padding: 30px 0 52px;
    display: flex;
    flex-direction: column;

    .popup__content {
      overflow-y: scroll;
    }

    .vue-pdf-embed > div {
      overflow: hidden;
    }

    canvas {
      scale: 1.2;
    }

    .textLayer {
      scale: 1.2;
    }
  }

  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 30px;
    height: 30px;
    margin-bottom: 44px;
  }

  &__title {
    font-weight: 600;
    font-size: 16px;
    line-height: 18px;
    color: #585f6d;
  }

  &__close {
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
  }

  &__buttons {
    padding: 0 70px;
  }

  &__input {
    margin-top: 60px;
    padding: 0 52px;
  }

  &__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 7px;
    padding: 0 100px;

    &-item {
      padding: 25px;
    }
  }
}
</style>
